import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import Modal from 'components/Modal';
import styled, { css } from 'styled-components';
import appointmentSelectors from 'features/appointment/services/selectors';
import { Col, Row } from 'antd';
import { formatCurrency } from 'utils/formatCurrency';
import IcCheckService from './IcCheckService';
import { IServiceItemData } from 'services/shop/types/categories';
import colorTheme from 'constants/color';

export type ChangeServiceModalRef = {
  open: (
    callback?: (
      serviceId?: string,
      serviceObject?: IServiceItemData,
      serviceIds?: string[],
      serviceObjects?: IServiceItemData[],
      staffId?: string
    ) => void,
    selectedServiceId?: string,
    staffId?: string,
    selectedServiceIds?: string[],
  ) => void;
};

const ChangeServiceModal = forwardRef<
  ChangeServiceModalRef,
  { modalTitle?: string; isMultiple?: boolean }
>((props, ref) => {
  const [visible, setVisible] = useState(false);
  const callbackRef = useRef<
    | ((serviceId?: string, serviceObject?: IServiceItemData, serviceIds?: string[], serviceObjects?: IServiceItemData[], staffId?: string) => void)
    | undefined
  >(undefined);
  const categoriesBase = appointmentSelectors.getCategories();
  const listServiceAll = useMemo(() => {
    return categoriesBase.flatMap((category) => category.services);
  }, [categoriesBase]);
  const [selectedServiceId, setSelectedServiceId] = useState<string[]>([]);
  const [categorySelected, setCategorySelected] = useState('');
  const staffIdRef = useRef<string>();
  
  const handleClickService = (serviceId: string) => {
    if (props?.isMultiple) {
      if (selectedServiceId.includes(serviceId)) {
        setSelectedServiceId(selectedServiceId.filter((id) => id !== serviceId));
      } else {
        setSelectedServiceId([...selectedServiceId, serviceId]);
      }
    } else {
      setSelectedServiceId([serviceId]);
    }
  };

  const servicesFilterByCategory = useMemo(() => {
    return categoriesBase.find((category) => category.id === categorySelected)?.services;
  }, [categoriesBase, categorySelected]);

  const handleClose = () => {
    setVisible(false);
  };
  const onSubmit = () => {
    setVisible(false);
    if (props?.isMultiple) {
      const serviceObjects = listServiceAll?.filter((service) =>
        selectedServiceId.includes(service.id)
      );
      callbackRef.current?.(undefined, undefined, selectedServiceId, serviceObjects, staffIdRef.current);
    } else {
      const serviceObject = servicesFilterByCategory?.filter((service) =>
        selectedServiceId.includes(service.id)
      );
      callbackRef.current?.(selectedServiceId[0], serviceObject?.[0], undefined, undefined, staffIdRef.current);
    }
  };

  useImperativeHandle(ref, () => ({
    open: (callback?: () => void, selectedServiceId?: string, staffId?: string, selectedServiceIds?: string[],) => {
      staffIdRef.current = staffId;
      if (!selectedServiceId && !selectedServiceIds) {
        setCategorySelected(categoriesBase?.[0]?.id || '');
        setSelectedServiceId([]);
      } else {
        const categorySelected = categoriesBase.find((category) =>
          category.services.some((service) => service.id === selectedServiceId)
        );

        setCategorySelected(categorySelected?.id || categoriesBase?.[0]?.id || '');
        if (props?.isMultiple) {
          setSelectedServiceId(selectedServiceIds || []);
        } else {
          setSelectedServiceId(selectedServiceId ? [selectedServiceId] : []);
        }
      }
      callbackRef.current = callback;
      setVisible(true);
    },
  }));

  return (
    <Modal
      modalTitle={props.modalTitle || 'Change services'}
      visible={visible}
      onClose={handleClose}
      onSubmit={onSubmit}
      width={'1600px'}
      noneBodyStyle={true}
      noneFooterStyle={true}
      footerPadding={0.5}
      okTitle='OK'
      v2={true}
    >
      <Container>
        <CateGoryContainer>
          {categoriesBase.map((category) => (
            <CategoryItem
              key={category.id}
              selected={categorySelected === category.id}
              onClick={() => setCategorySelected(category.id)}
            >
              <div className='text'>{category.categoryName}</div>
            </CategoryItem>
          ))}
        </CateGoryContainer>
        <ServicesContainer>
          <Row gutter={[16, 8]}>
            {servicesFilterByCategory?.map((service) => (
              <Col xs={24} sm={24} md={24} lg={8} xl={8} key={service.id}>
                <ServiceItem
                  selected={selectedServiceId.includes(service.id)}
                  activeColor={service.backgroundColor ?? '#FF9494'}
                  onClick={() => handleClickService(service.id)}
                >
                  <div className='color-service' style={{ backgroundColor: service.backgroundColor }} />
                  <div className='name'>{service.serviceName}</div>
                  <div className='price'>{formatCurrency(service.priceSell)}</div>
                  {selectedServiceId.includes(service.id) && (
                    <div className='icon'>
                      <IcCheckService width={20} height={20} />
                    </div>
                  )}
                </ServiceItem>
              </Col>
            ))}
          </Row>
        </ServicesContainer>
      </Container>
    </Modal>
  );
});
ChangeServiceModal.displayName = 'ChangeServiceModal';

export default ChangeServiceModal;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CateGoryContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(175px, 1fr));
  // gap: 10px;
  padding: 1rem;
`;

const CategoryItem = styled.div<{ selected: boolean }>`
  display: flex;
  padding: 25px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
  color: #505050;
  text-align: center;
  font-family: Poppins;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 30px;
  text-transform: capitalize;
  background: #fcfcfd;
  box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3),
    0 -3px 0 0 #d6d6e7 inset;
  cursor: pointer;

  ${({ selected }) =>
    selected &&
    css`
      color: #fff;
      background: #2d6772;
      box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3),
        0 -3px 0 0 rgba(8, 56, 65, 0.5) inset;
    `}
  .text {
    font-size: 18px;
    font-weight: 600;
    line-height: 30px;
    text-transform: capitalize;
    ${({ selected }) =>
      selected &&
      css`
        color: #fff;
      `}
  }
`;

const ServicesContainer = styled.div`
  height: 700px;
  overflow-y: auto;
  padding: 0 1rem;
`;

const ServiceItem = styled.div<{
  selected: boolean;
  activeColor: string;
}>`
  position: relative;
  cursor: pointer;
  height: 56px;
  padding: 0 12px 0 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-radius: 5px;
  box-sizing: border-box;

  background: ${({ selected, activeColor }) =>
    selected
      ? `color-mix(in srgb, ${activeColor} 8%, white)`
      : colorTheme.v2_neutral_2};

  border: 2px solid
    ${({ selected, activeColor }) =>
      selected ? activeColor : 'transparent'};

  box-shadow: ${({ selected }) =>
    selected
      ? '0 1px 3px rgba(0, 0, 0, 0.08)'
      : '0 2px 3px rgba(0, 0, 0, 0.12)'};

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  .color-service {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 6px;
    height: 65%;
    border-radius: 16px;
    flex-shrink: 0;
  }

  .name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    color: #1d2129;
    font-family: Poppins;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 18px;
    font-weight: 400;
    line-height: normal;
  }

  .price {
    flex-shrink: 0;
    color: #1d2129;
    font-size: 18px;
    font-weight: 600;
    white-space: nowrap;
  }

  .icon {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border-radius: 50%;
    background: ${({ activeColor }) => activeColor};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 6px;
  }
`;
